import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../../ui/dialog";
import { EDSButton } from "../../../../../eds/EDSButton";
import { Label } from "../../../../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../../ui/radio-group";

export const DELETE_REASONS = [
  { value: "spam", label: "Spam" },
  { value: "duplicate", label: "Duplicate" },
  { value: "other", label: "Other" }
] as const;

export type DeleteReason = (typeof DELETE_REASONS)[number]["value"];

interface DeleteBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: DeleteReason) => void;
  isDeleting: boolean;
}

const DeleteBookingDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isDeleting
}: DeleteBookingDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<DeleteReason>("other");

  const handleConfirm = () => {
    onConfirm(selectedReason);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedReason("other");
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        aria-describedby="delete-dialog-description"
      >
        <DialogHeader>
          <DialogTitle>Delete booking</DialogTitle>
          <DialogDescription id="delete-dialog-description">
            Are you sure you want to delete this booking? This action can be
            undone by reopening the booking later.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Label className="text-sm font-medium">
            Reason for deletion (optional):
          </Label>
          <RadioGroup
            value={selectedReason}
            onValueChange={(value: string) =>
              setSelectedReason(value as DeleteReason)
            }
            className="mt-2"
            aria-label="Reason for deletion"
          >
            {DELETE_REASONS.map((reason) => (
              <div key={reason.value} className="flex items-center space-x-2">
                <RadioGroupItem
                  value={reason.value}
                  id={`delete-reason-${reason.value}`}
                />
                <Label htmlFor={`delete-reason-${reason.value}`}>
                  {reason.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter>
          <EDSButton
            variant="outline"
            size="md"
            onClick={() => handleOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </EDSButton>
          <EDSButton
            variant="destructive"
            size="md"
            onClick={handleConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteBookingDialog;
